import React from "react";
import { TextField } from "@mui/material";
import { AGE_GROUP_OPTIONS } from "../../../constants/careContextOptions";
import {
  deriveAgeGroupFromDateOfBirth,
  deriveAgeLabelFromDateOfBirth,
} from "../../../utils/profileAge";

const pad2 = (value) => String(value).padStart(2, "0");

const isValidDateParts = ({ year, month, day }) => {
  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day;
};

export const formatDateOfBirthForDisplay = (value = "") => {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value || "";

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

export const parseDateOfBirthInput = (value = "") => {
  const cleanValue = String(value || "").trim();
  const isoMatch = cleanValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const slashMatch = cleanValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const match = isoMatch || slashMatch;
  if (!match) return "";

  const year = Number(isoMatch ? match[1] : match[3]);
  const month = Number(isoMatch ? match[2] : match[1]);
  const day = Number(isoMatch ? match[3] : match[2]);
  if (!isValidDateParts({ year, month, day })) {
    return "";
  }

  return `${year}-${pad2(month)}-${pad2(day)}`;
};

export const formatDateOfBirthInput = (value = "") => {
  const isoDate = parseDateOfBirthInput(value);
  if (isoDate) {
    return formatDateOfBirthForDisplay(isoDate);
  }

  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const ProfileDateOfBirthFields = ({
  dateOfBirth = "",
  onDateOfBirthChange,
  fallbackAgeGroup = "",
  fieldSx = {},
}) => {
  const [displayDateOfBirth, setDisplayDateOfBirth] = React.useState(
    formatDateOfBirthForDisplay(dateOfBirth)
  );
  const derivedAgeGroup = deriveAgeGroupFromDateOfBirth(dateOfBirth);
  const derivedAgeLabel = deriveAgeLabelFromDateOfBirth(dateOfBirth);
  const resolvedAgeGroup = derivedAgeGroup || fallbackAgeGroup;
  const ageGroupLabel = AGE_GROUP_OPTIONS.find((option) => option.value === resolvedAgeGroup)?.label
    || "Enter date of birth first";

  React.useEffect(() => {
    setDisplayDateOfBirth(formatDateOfBirthForDisplay(dateOfBirth));
  }, [dateOfBirth]);

  const handleDateOfBirthChange = (event) => {
    const nextDisplayValue = formatDateOfBirthInput(event.target.value);
    setDisplayDateOfBirth(nextDisplayValue);

    if (!nextDisplayValue.trim()) {
      onDateOfBirthChange?.("");
      return;
    }

    const parsedDateOfBirth = parseDateOfBirthInput(nextDisplayValue);
    if (parsedDateOfBirth) {
      onDateOfBirthChange?.(parsedDateOfBirth);
    }
  };

  return (
    <>
      <TextField
        label="Date of birth *"
        variant="outlined"
        fullWidth
        type="text"
        value={displayDateOfBirth}
        onChange={handleDateOfBirthChange}
        placeholder="MM/DD/YYYY"
        InputLabelProps={{ shrink: true }}
        inputProps={{
          inputMode: "numeric",
          autoComplete: "bday",
          pattern: "[0-9]*",
          maxLength: 10,
        }}
        helperText="Enter 8 digits. We will format it as MM/DD/YYYY."
        sx={fieldSx}
      />
      <TextField
        label="Age group"
        variant="outlined"
        fullWidth
        value={ageGroupLabel}
        InputProps={{ readOnly: true }}
        helperText={derivedAgeLabel ? `Age ${derivedAgeLabel}, based on date of birth.` : "Not set yet."}
        sx={fieldSx}
      />
    </>
  );
};

export default ProfileDateOfBirthFields;
